import supabase, { isSupabaseConfigured } from "./supabaseClient.js";

export const TRIP_TYPES_STORAGE_KEY = "trishul_trip_types";
export const BOOKING_VEHICLES_STORAGE_KEY = "trishul_booking_vehicles";

export const seedTripTypes = [
  {
    id: "trip-type-one-way",
    name: "One Way Trip",
    code: "ONE_WAY",
    requires_return_journey: false,
    active: true,
    display_order: 1,
    created_at: "2026-01-01T00:00:00.000Z",
    updated_at: "2026-01-01T00:00:00.000Z",
  },
  {
    id: "trip-type-round-trip",
    name: "Round / Return Trip",
    code: "ROUND_TRIP",
    requires_return_journey: true,
    active: true,
    display_order: 2,
    created_at: "2026-01-01T00:00:00.000Z",
    updated_at: "2026-01-01T00:00:00.000Z",
  },
];

export const seedBookingVehicles = [
  {
    id: "veh-any",
    name: "Any",
    category: "Any",
    code: "ANY",
    active: true,
    display_order: 1,
    created_at: "2026-01-01T00:00:00.000Z",
    updated_at: "2026-01-01T00:00:00.000Z",
  },
  {
    id: "veh-swift-dzire",
    name: "Swift Dzire",
    category: "Sedan",
    code: "SWIFT_DZIRE",
    active: true,
    display_order: 2,
    created_at: "2026-01-01T00:00:00.000Z",
    updated_at: "2026-01-01T00:00:00.000Z",
  },
  {
    id: "veh-hyundai-aura",
    name: "Hyundai Aura",
    category: "Sedan",
    code: "HYUNDAI_AURA",
    active: true,
    display_order: 3,
    created_at: "2026-01-01T00:00:00.000Z",
    updated_at: "2026-01-01T00:00:00.000Z",
  },
  {
    id: "veh-maruti-ertiga",
    name: "Maruti Ertiga",
    category: "MUV/MPV",
    code: "MARUTI_ERTIGA",
    active: true,
    display_order: 4,
    created_at: "2026-01-01T00:00:00.000Z",
    updated_at: "2026-01-01T00:00:00.000Z",
  },
  {
    id: "veh-innova-crysta",
    name: "Toyota Innova Crysta",
    category: "Premium SUV",
    code: "INNOVA_CRYSTA",
    active: true,
    display_order: 5,
    created_at: "2026-01-01T00:00:00.000Z",
    updated_at: "2026-01-01T00:00:00.000Z",
  },
  {
    id: "veh-force-traveller",
    name: "Force Traveller",
    category: "Traveller",
    code: "FORCE_TRAVELLER",
    active: true,
    display_order: 6,
    created_at: "2026-01-01T00:00:00.000Z",
    updated_at: "2026-01-01T00:00:00.000Z",
  },
  {
    id: "veh-force-urbania",
    name: "Force Urbania",
    category: "Traveller",
    code: "FORCE_URBANIA",
    active: true,
    display_order: 7,
    created_at: "2026-01-01T00:00:00.000Z",
    updated_at: "2026-01-01T00:00:00.000Z",
  },
];

/**
 * Returns the exact display label required by the client specifications:
 * - Any
 * - Swift Dzire (Sedan)
 * - Hyundai Aura (Sedan)
 * - Maruti Ertiga (MUV/MPV)
 * - Toyota Innova Crysta (Premium SUV)
 * - Force Traveller
 * - Force Urbania
 */
export function formatVehicleLabel(vehicle) {
  if (!vehicle) return "";
  if (vehicle.name.toLowerCase() === "any") return "Any";
  if (
    !vehicle.category ||
    vehicle.category.toLowerCase() === "any" ||
    vehicle.category.toLowerCase() === "traveller"
  ) {
    return vehicle.name;
  }
  return `${vehicle.name} (${vehicle.category})`;
}

// -------------------------------------------------------------
// Local Storage Helpers
// -------------------------------------------------------------
function readLocal(key, seed) {
  try {
    if (typeof localStorage === "undefined") return seed;
    const raw = localStorage.getItem(key);
    if (!raw) {
      localStorage.setItem(key, JSON.stringify(seed));
      return seed;
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : seed;
  } catch (err) {
    console.warn(`Could not read ${key} from storage:`, err);
    return seed;
  }
}

function writeLocal(key, data) {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(key, JSON.stringify(data));
    }
  } catch (err) {
    console.warn(`Could not write ${key} to storage:`, err);
  }
}

// -------------------------------------------------------------
// Trip Types API
// -------------------------------------------------------------

export async function listAllTripTypes() {
  const localList = readLocal(TRIP_TYPES_STORAGE_KEY, seedTripTypes);
  if (!isSupabaseConfigured) {
    return { data: localList.sort((a, b) => a.display_order - b.display_order), error: null };
  }

  try {
    const { data, error } = await supabase
      .from("trip_types")
      .select("*")
      .order("display_order", { ascending: true });

    if (!error && Array.isArray(data) && data.length > 0) {
      writeLocal(TRIP_TYPES_STORAGE_KEY, data);
      return { data, error: null };
    }
  } catch (err) {
    console.warn("Supabase trip_types query error, using local fallback:", err);
  }

  return { data: localList.sort((a, b) => a.display_order - b.display_order), error: null };
}

export async function listPublicTripTypes() {
  const result = await listAllTripTypes();
  const active = (result.data || []).filter((item) => item.active);
  return { data: active, error: result.error };
}

export async function saveTripType(tripType) {
  const localList = readLocal(TRIP_TYPES_STORAGE_KEY, seedTripTypes);
  const cleanCode = String(tripType.code || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9_]/g, "_");

  if (!cleanCode) {
    return { data: null, error: new Error("Trip type code is required.") };
  }
  if (!tripType.name || !tripType.name.trim()) {
    return { data: null, error: new Error("Trip type name is required.") };
  }

  // Check code uniqueness (excluding current record if editing)
  const duplicate = localList.find(
    (item) => item.code === cleanCode && item.id !== tripType.id
  );
  if (duplicate) {
    return {
      data: null,
      error: new Error(`Trip type code "${cleanCode}" is already in use.`),
    };
  }

  const now = new Date().toISOString();
  const canonical = {
    id: tripType.id || `trip-type-${cleanCode.toLowerCase().replace(/_/g, "-")}-${Date.now()}`,
    name: tripType.name.trim(),
    code: cleanCode,
    requires_return_journey: Boolean(tripType.requires_return_journey),
    active: tripType.active !== false,
    display_order: Number(tripType.display_order) || localList.length + 1,
    updated_at: now,
    created_at: tripType.created_at || now,
  };

  // Update local
  const existingIdx = localList.findIndex((item) => item.id === canonical.id || item.code === canonical.code);
  let updatedLocal;
  if (existingIdx >= 0) {
    updatedLocal = [...localList];
    updatedLocal[existingIdx] = { ...updatedLocal[existingIdx], ...canonical };
  } else {
    updatedLocal = [...localList, canonical];
  }
  updatedLocal.sort((a, b) => a.display_order - b.display_order);
  writeLocal(TRIP_TYPES_STORAGE_KEY, updatedLocal);

  // Sync with Supabase if available
  if (isSupabaseConfigured) {
    try {
      const { data, error } = await supabase
        .from("trip_types")
        .upsert(canonical, { onConflict: "code" })
        .select()
        .maybeSingle();

      if (!error && data) {
        return { data, error: null };
      }
    } catch (err) {
      console.warn("Could not sync trip_type to Supabase:", err);
    }
  }

  return { data: canonical, error: null };
}

export async function toggleTripTypeStatus(id, active) {
  const localList = readLocal(TRIP_TYPES_STORAGE_KEY, seedTripTypes);
  
  // Safeguard: Prevent deactivation of the last active trip type
  if (!active) {
    const activeCount = localList.filter((item) => item.active && item.id !== id).length;
    if (activeCount === 0) {
      return {
        data: null,
        error: new Error("Cannot deactivate: At least one active trip type must remain available for bookings."),
      };
    }
  }

  const target = localList.find((item) => item.id === id);
  if (!target) {
    return { data: null, error: new Error("Trip type not found.") };
  }

  return saveTripType({ ...target, active });
}

// -------------------------------------------------------------
// Booking Vehicles API
// -------------------------------------------------------------

export async function listAllBookingVehicles() {
  const localList = readLocal(BOOKING_VEHICLES_STORAGE_KEY, seedBookingVehicles);
  if (!isSupabaseConfigured) {
    return { data: localList.sort((a, b) => a.display_order - b.display_order), error: null };
  }

  try {
    const { data, error } = await supabase
      .from("booking_vehicles")
      .select("*")
      .order("display_order", { ascending: true });

    if (!error && Array.isArray(data) && data.length > 0) {
      writeLocal(BOOKING_VEHICLES_STORAGE_KEY, data);
      return { data, error: null };
    }
  } catch (err) {
    console.warn("Supabase booking_vehicles query error, using local fallback:", err);
  }

  return { data: localList.sort((a, b) => a.display_order - b.display_order), error: null };
}

export async function listPublicBookingVehicles() {
  const result = await listAllBookingVehicles();
  const active = (result.data || []).filter((item) => item.active);
  return { data: active, error: result.error };
}

export async function saveBookingVehicle(vehicle) {
  const localList = readLocal(BOOKING_VEHICLES_STORAGE_KEY, seedBookingVehicles);
  const cleanCode = String(vehicle.code || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9_]/g, "_");

  if (!cleanCode) {
    return { data: null, error: new Error("Vehicle code is required.") };
  }
  if (!vehicle.name || !vehicle.name.trim()) {
    return { data: null, error: new Error("Vehicle name is required.") };
  }
  if (!vehicle.category || !vehicle.category.trim()) {
    return { data: null, error: new Error("Vehicle category is required.") };
  }

  // Check code uniqueness
  const duplicate = localList.find(
    (item) => item.code === cleanCode && item.id !== vehicle.id
  );
  if (duplicate) {
    return {
      data: null,
      error: new Error(`Vehicle code "${cleanCode}" is already in use.`),
    };
  }

  // Safeguard: Prevent duplicate active "Any" options
  const isAny =
    vehicle.name.trim().toLowerCase() === "any" ||
    cleanCode === "ANY";
  if (isAny && vehicle.active !== false) {
    const existingAny = localList.find(
      (item) =>
        item.id !== vehicle.id &&
        item.active &&
        (item.name.toLowerCase() === "any" || item.code === "ANY")
    );
    if (existingAny) {
      return {
        data: null,
        error: new Error('Only one active "Any" vehicle preference option is allowed.'),
      };
    }
  }

  const now = new Date().toISOString();
  const canonical = {
    id: vehicle.id || `veh-${cleanCode.toLowerCase().replace(/_/g, "-")}-${Date.now()}`,
    name: vehicle.name.trim(),
    category: vehicle.category.trim(),
    code: cleanCode,
    active: vehicle.active !== false,
    display_order: Number(vehicle.display_order) || localList.length + 1,
    updated_at: now,
    created_at: vehicle.created_at || now,
  };

  // Update local
  const existingIdx = localList.findIndex((item) => item.id === canonical.id || item.code === canonical.code);
  let updatedLocal;
  if (existingIdx >= 0) {
    updatedLocal = [...localList];
    updatedLocal[existingIdx] = { ...updatedLocal[existingIdx], ...canonical };
  } else {
    updatedLocal = [...localList, canonical];
  }
  updatedLocal.sort((a, b) => a.display_order - b.display_order);
  writeLocal(BOOKING_VEHICLES_STORAGE_KEY, updatedLocal);

  // Sync with Supabase if available
  if (isSupabaseConfigured) {
    try {
      const { data, error } = await supabase
        .from("booking_vehicles")
        .upsert(canonical, { onConflict: "code" })
        .select()
        .maybeSingle();

      if (!error && data) {
        return { data, error: null };
      }
    } catch (err) {
      console.warn("Could not sync booking_vehicle to Supabase:", err);
    }
  }

  return { data: canonical, error: null };
}

export async function toggleBookingVehicleStatus(id, active) {
  const localList = readLocal(BOOKING_VEHICLES_STORAGE_KEY, seedBookingVehicles);
  
  // Safeguard: Prevent deactivation of the final active vehicle
  if (!active) {
    const activeCount = localList.filter((item) => item.active && item.id !== id).length;
    if (activeCount === 0) {
      return {
        data: null,
        error: new Error("Cannot deactivate: At least one active vehicle option must remain available for bookings."),
      };
    }
  }

  const target = localList.find((item) => item.id === id);
  if (!target) {
    return { data: null, error: new Error("Vehicle not found.") };
  }

  return saveBookingVehicle({ ...target, active });
}
